/**
 * Profile Hooks
 *
 * Provides hooks for updating user profile and changing password.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { queryKeys } from './queryKeys';

interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  preferredLanguage?: string;
}

interface UpdateProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  isActive: boolean;
  preferredLanguage: string;
  whatsappNumber: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

/**
 * Hook for updating the current user's profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileData) => {
      const currentUser = useAuthStore.getState().user;
      if (!currentUser) throw new Error('Not authenticated');
      return api.patch<UpdateProfileResponse>(`/users/${currentUser.id}`, data);
    },
    onSuccess: (data) => {
      // Update auth store with new profile data
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().setUser({
          ...currentUser,
          firstName: data.firstName,
          lastName: data.lastName,
        });
      }
      // Invalidate current user query
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
    },
  });
}

/**
 * Hook for changing the current user's password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordData) => {
      const currentUser = useAuthStore.getState().user;
      if (!currentUser) throw new Error('Not authenticated');
      return api.patch<ChangePasswordResponse>(
        `/users/${currentUser.id}/change-password`,
        data
      );
    },
  });
}

export type { UpdateProfileData, ChangePasswordData };
